import { useMemo } from 'react';

import { useCreateBeneficiaryMutation } from '@/shared/generated/graphql';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { BeneficiaryDto } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';

export type BeneficiaryCreateData = Omit<BeneficiaryDto, 'id' | 'centralDbProfileId'>;

export const useBeneficiariesActions = () => {
  const keyPair = useKeyPairStore((s) => s.keyPair);
  const { create: beneficiariesCreate, ...beneficiariesActionsRest } =
    useSjActions('beneficiaries');

  const [createBeneficiaryMutation] = useCreateBeneficiaryMutation();

  return useMemo(() => {
    const create = async (data: BeneficiaryCreateData): Promise<BeneficiaryDto | null> => {
      if (!keyPair?.publicKey) {
        return null;
      }

      const centralDbBeneficiary = await createBeneficiaryMutation({
        variables: {
          email: data.email,
          dontSendInviteEmail: false,
        },
      });

      if (!centralDbBeneficiary.data?.createBeneficiary.account.profileId) {
        // TODO return error
        return null;
      }

      const dto: BeneficiaryDto = {
        id: beneficiariesActionsRest.generateId(),
        ...data,
        centralDbProfileId: centralDbBeneficiary?.data?.createBeneficiary.account.profileId,
      };

      await beneficiariesCreate(dto, [keyPair.publicKey]);

      return dto;
    };

    const findByApiId = (id: number): BeneficiaryDto | undefined => {
      return beneficiariesActionsRest.findOne((item) => item.centralDbProfileId === id);
    };

    return { create, findByApiId, ...beneficiariesActionsRest };
  }, [keyPair, createBeneficiaryMutation, beneficiariesActionsRest]);
};
